// © Microsoft Corporation. All rights reserved.

import { Telemetry, TelemetryAttrName, TelemetryObjectBase } from "@msnews/telemetry-contracts";

import React from "react";

/**
 * Returns an HOC for tracking telemetry of a React.Component.
 * @param WrappedComponent - the component to track telemetry.
 * @param telemetryObject - the telemetry object of the component.
 */
export function trackComponent<TProps, TState>(
    WrappedComponent: React.ComponentType<TProps>,
    telemetryObject: TelemetryObjectBase
): React.ComponentClass<TProps> {

    return class ComponentTracker extends React.Component<TProps, TState, never> {
        /** Telemetry object for the component. */
        private telemetryObject: TelemetryObjectBase;

        constructor(props: TProps, context?: any) {
            super(props, context);
            this.telemetryObject = telemetryObject;
        }

        /** React render lifecycle handler  */
        public render(): JSX.Element {
            return <WrappedComponent {...this.props} {...this.telemetryObject.render()} />;
        }

        /** React componentWillUnmount lifecycle handler  */
        public componentWillUnmount(): void {
            // TODO: This line causes an issue that create same full id when component unmounted
            // Comment it out for now as workaround for EXP.
            // if (this.telemetryObject) {
            //     this.telemetryObject.delete();
            // }
        }

        /** React componentDidMount lifecycle handler  */
        public componentDidMount(): void {
            if (Telemetry && Telemetry.config && Telemetry.config.autoCaptureContentView) {
                const elem = document.querySelector("[" + TelemetryAttrName + "*='\"i\":\"" + this.telemetryObject.fullId + "\"'" + "]");

                if (elem) {
                    Telemetry.observeSingleElement(elem as HTMLElement);
                    Telemetry.observeElementTree(elem as HTMLElement);
                }
            }
        }

        /** React componentDidUpdate lifecycle handler  */
        public componentDidUpdate(): void {
            // TODO: avoid calling this everytime component get updated
            if (Telemetry && Telemetry.config && Telemetry.config.autoCaptureContentView) {
                const elem = document.querySelector("[" + TelemetryAttrName + "*='\"i\":\"" + this.telemetryObject.fullId + "\"'" + "]");

                if (elem) {
                    Telemetry.observeSingleElement(elem as HTMLElement);
                    Telemetry.observeElementTree(elem as HTMLElement);
                }
            }
        }
    };
}